import { useQuery, UseQueryResult } from 'react-query';
import { DeviceResponseType } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import { ResponseResult } from 'lavva.exalushome/build/js/Services/FieldChangeResult';
import { StateHistoryErrorCode } from 'lavva.exalushome/build/js/Services/StatesHistory/IStatesHistoryService';
import { StateDataResponse, StateInterval } from 'lavva.exalushome/build/js/Services/StatesHistory/StatesHistory';
import { useNavigateState } from '../components/channel/hooks/use-history-state';
import { useExalusContext } from '../context';
import { useExalusServicesContext } from '../context/services';

export const useChartSupportPagination = (): UseQueryResult<boolean> => {
  const { connected, selectedExalusCredentials, synchronized } = useExalusContext();
  const { historyStatesApi } = useExalusServicesContext();

  return useQuery<boolean>(
    ['chart-pagination-supported', selectedExalusCredentials?.id, synchronized],
    async () => {
      return await historyStatesApi.DoesSupportPreciseTimeFramesAsync();
    },
    {
      enabled: Boolean(connected && selectedExalusCredentials?.id && synchronized),
      retry: true,
      cacheTime: 0,
    },
  );
};

export const useChartData = <T>(
  channel: IDeviceChannel,
  responseType: DeviceResponseType,
  stateFrom: StateInterval,
  limit: number,
  offSet: number,
): UseQueryResult<StateDataResponse<T> | undefined> => {
  const { connected, selectedExalusCredentials, synchronized } = useExalusContext();
  const { historyStatesApi } = useExalusServicesContext();
  const { handleHistoryError } = useNavigateState(channel);

  return useQuery<StateDataResponse<T> | undefined>(
    ['history-chart-data', selectedExalusCredentials?.id, synchronized, stateFrom, offSet, channel.ChannelId],
    async () => {
      try {
        const result = await historyStatesApi.GetStatesByIntervalAsync<T>(
          channel.GetDevice(),
          channel.Number,
          responseType,
          stateFrom,
          limit,
          offSet,
        );

        if ((result as ResponseResult<StateHistoryErrorCode>).Type) {
          handleHistoryError(result as ResponseResult<StateHistoryErrorCode>);
          return undefined;
        }

        return result as StateDataResponse<T>;
      } catch (error) {
        console.error('Error fetching chart data:', error);
        return undefined;
      }
    },
    {
      enabled: Boolean(connected && selectedExalusCredentials?.id && synchronized),
      retry: true,
      cacheTime: 0,
    },
  );
};
